import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createCreation,
  uploadArtifact,
  finalizeInput,
  dciError,
  INPUT_METHOD,
} from "../../Services/dialogueCreations";
import { CaptureHeader, CapturePage, useFocusedCaptureHeader, makeIdempotencyKey } from "./shared";
import { DOCUMENT_COPY, errorText } from "./copy";
import pptIcon from "../../Assets/icon/capture/ppt.svg";
import wordIcon from "../../Assets/icon/capture/word.svg";
import pdfIcon from "../../Assets/icon/capture/pdf.svg";
import fileIcon from "../../Assets/icon/capture/file.svg";
import closeIcon from "../../Assets/icon/capture/close.svg";

/** What a document creation accepts (guide §3.3): PDF/DOCX/PPTX/TXT/RTF/MD. */
const ACCEPT = ".pdf,.docx,.pptx,.txt,.rtf,.md,application/pdf";

const formatSize = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Upload a Document (Figma 9856:14698 / 9856:14892). The file is chosen
 * first; Continue creates the DOCUMENT creation, uploads, and finalizes. A PDF
 * the server refuses at upload (too long, encrypted, corrupt) is reported here
 * with its stable code, not minutes later.
 */
function CaptureDocument() {
  const navigate = useNavigate();
  useFocusedCaptureHeader();
  const input = useRef(null);
  const key = useRef(makeIdempotencyKey("document"));
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const choose = (f) => {
    if (!f) return;
    setError(null);
    setFile(f);
  };

  const submit = async () => {
    if (!file || progress !== null) return;
    setError(null);
    setProgress(0);
    try {
      const creation = await createCreation(INPUT_METHOD.DOCUMENT, key.current);
      const id = creation?.creation_request_id;
      const uploaded = await uploadArtifact(id, file, setProgress);
      const seconds = uploaded?.artifact?.estimated_seconds;
      if (Number.isFinite(seconds) && seconds > 60) {
        setNote(DOCUMENT_COPY.longRead(Math.ceil(seconds / 60)));
      }
      await finalizeInput(id);
      navigate(`/capture/${encodeURIComponent(id)}`, { replace: true });
    } catch (e) {
      setError(errorText(dciError(e).code));
      setProgress(null);
    }
  };

  return (
    <CapturePage focused>
      <CaptureHeader title={DOCUMENT_COPY.title} />
      <div className="cap-body">
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            choose(e.target.files?.[0]);
            e.target.value = "";
          }}
        />

        {file ? (
          <div className="cap-card cap-file">
            <span className="cap-tile cap-tile--blue">
              <img src={/\.docx?$/i.test(file.name) ? wordIcon : fileIcon} alt="" width="24" height="24" />
            </span>
            <span className="cap-file__text">
              <span className="cap-file__name">{file.name}</span>
              <span className="cap-file__size">{formatSize(file.size)}</span>
            </span>
            {progress === null && (
              <button
                type="button"
                className="cap-icon-btn"
                aria-label={DOCUMENT_COPY.remove}
                onClick={() => setFile(null)}
              >
                <img src={closeIcon} alt="" width="20" height="20" />
              </button>
            )}
            {progress !== null && (
              <span className="cap-file__bar" style={{ width: `${Math.round(progress * 100)}%` }} />
            )}
          </div>
        ) : (
          <button
            type="button"
            className={`cap-card cap-drop${dragging ? " cap-drop--over" : ""}`}
            onClick={() => input.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              choose(e.dataTransfer.files?.[0]);
            }}
          >
            <span className="cap-drop__tiles" aria-hidden="true">
              <span className="cap-tile cap-tile--orange cap-tile--tilt-left">
                <img src={pptIcon} alt="" width="24" height="24" />
              </span>
              <span className="cap-tile cap-tile--blue">
                <img src={wordIcon} alt="" width="24" height="24" />
              </span>
              <span className="cap-tile cap-tile--teal cap-tile--tilt-right">
                <img src={pdfIcon} alt="" width="24" height="24" />
              </span>
            </span>
            <span className="cap-drop__title">{DOCUMENT_COPY.drop}</span>
            <span className="cap-drop__sub">{DOCUMENT_COPY.browse}</span>
            <span className="cap-drop__types">{DOCUMENT_COPY.types}</span>
          </button>
        )}

        {error && <p className="cap-error">{error}</p>}
        {note && <p className="cap-note">{note}</p>}

        {file ? (
          <button type="button" className="cap-btn" disabled={progress !== null} onClick={submit}>
            {DOCUMENT_COPY.continue}
          </button>
        ) : (
          <button type="button" className="cap-btn" onClick={() => input.current?.click()}>
            {DOCUMENT_COPY.browseCta}
          </button>
        )}
      </div>
    </CapturePage>
  );
}

export default CaptureDocument;
